import MainLayout from "./MainLayout";
import UseCase1 from "../assets/images/UC1.png";
import UseCase2 from "../assets/images/UC2.png";
import UseCase3 from "../assets/images/UC2.0.png";
import UseCase4 from "../assets/images/UC4.png";

const GRADIENT_TEXT =
  "bg-gradient-to-r from-blue-500 to-pink-500 bg-clip-text text-transparent pb-1 ";

const UseCase = ({ isInDashboard = false }) => {
  const content = (
    <main className={`w-full max-w-7xl mx-auto flex flex-col items-center text-center px-6 md:px-12 space-y-24 pb-24 ${isInDashboard ? "pt-9" : "pt-32"}`}>
      <div className="space-y-4">
        <h1 className="text-5xl md:text-7xl font-extrabold text-gray-200 leading-tight">
          Use Cases
        </h1>
        <p className="text-xl text-gray-400">
          Explore how XynexAI delivers value across various industries.
        </p>
      </div>

      {/* Case 1 */}
      <section className="w-full text-left space-y-24">
        {/* PROBLEM SECTION */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src={UseCase1}
              alt="Inventory analytics"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="space-y-6 text-left">
            <h2 className={`text-3xl md:text-4xl font-bold ${GRADIENT_TEXT}`}>
              Detecting Inventory Pilferage Hidden in Plain Sight
            </h2>

            <p className="text-gray-200 leading-relaxed">
              Inventory pilferage rarely surfaces as a single, obvious
              exception. Instead, it hides within repeated small variances,
              abnormal consumption trends, or frequent adjustments that appear
              operationally justified in isolation.
            </p>

            <p className="text-gray-200 leading-relaxed">
              Over time, these subtle patterns lead to steady inventory
              shrinkage, leaving supply chain finance teams concerned but
              without clear visibility into where and why losses are occurring.
            </p>
          </div>
        </div>

        {/* HOW XYNEX HELPS */}
        <div className="max-w-4xl mx-auto text-left space-y-6">
          <h3 className={`text-2xl font-semibold ${GRADIENT_TEXT}`}>
            How Xynex Identifies It
          </h3>

          <p className="text-gray-200 leading-relaxed">
            Xynex analyzes inventory data by allowing users to define key
            variables such as material, batch, movement type, quantity, user,
            and time. It evaluates how these variables behave together and
            highlights patterns that deviate from normal operational behavior.
          </p>

          <p className="text-gray-200 leading-relaxed">
            GenAI then explains which variable combinations indicate abnormal
            usage and why they deserve attention — in simple, business-friendly
            language.
          </p>
          <div className="space-y-4">
            <h3 className={`text-2xl font-semibold ${GRADIENT_TEXT}`}>
              The Impact
            </h3>

            <p className="text-gray-200 leading-relaxed">
              Losses that once blended into routine operations become visible
              early, enabling preventive action rather than post-fact
              investigation.
            </p>
          </div>
        </div>
      </section>
      {/* Case 2 */}
      <section className="w-full text-left space-y-24">
        {/* PROBLEM SECTION */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          <div className="space-y-6 text-left">
            <h2 className={`text-3xl md:text-4xl font-bold ${GRADIENT_TEXT}`}>
              Uncovering Excessive or Unjustified Discounts
            </h2>

            <p className="text-gray-200 leading-relaxed">
              Everything looked fine at first. Discounts were approved, sales
              were happening, and plans were in place. But margins kept going
              down. When the Commercial Finance Manager looked closer, one thing
              felt off. Some products were selling in much higher volumes only
              when bigger discounts were given. Each deal made sense on its own,
              but together the volume and discount mix looked unusual.
            </p>

            <p className="text-gray-200 leading-relaxed">
              There was no clear report to show where this was happening or why.
              The concern wasn’t about breaking policy—it was about not being
              able to see the real reason margins were quietly shrinking.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src={UseCase3}
              alt="Inventory analytics"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* HOW XYNEX HELPS */}
        <div className="max-w-4xl mx-auto text-left space-y-6">
          <h3 className={`text-2xl font-semibold ${GRADIENT_TEXT}`}>
            How Xynex Identifies It
          </h3>

          <p className="text-gray-200 leading-relaxed">
            The Commercial Finance Manager uploaded the combined sales and
            discount data into Xynex and selected a few basic fields—product,
            customer, Qty and variable field as discount. Xynex compared how
            volume and discount normally move together and then flagged specific
            records where this relationship looked unusual. It didn’t just say
            “there is a problem”—it showed the exact invoices and
            customer–product combinations where high volumes were linked to
            unusually high discounts.
          </p>

          <p className="text-gray-200 leading-relaxed">
            For each case, Xynex explained in simple terms why these records
            stood out from the rest and how they were connected to the overall
            margin drop. This made it easy for the team to review the right
            transactions, have focused discussions with sales, and correct the
            issue without guesswork.
          </p>
          <div className="space-y-4">
            <h3 className={`text-2xl font-semibold ${GRADIENT_TEXT}`}>
              The Impact
            </h3>

            <p className="text-gray-200 leading-relaxed">
              Organizations regain pricing discipline without restricting
              legitimate sales flexibility.
            </p>
          </div>
        </div>
      </section>
      {/* Case 3 */}
      <section className="w-full text-left space-y-24">
        {/* PROBLEM SECTION */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src={UseCase2}
              alt="Inventory analytics"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="space-y-6 text-left">
            <h2 className={`text-3xl md:text-4xl font-bold ${GRADIENT_TEXT}`}>
              Identifying Expensive or Inefficient Procurement
            </h2>

            <p className="text-gray-200 leading-relaxed">
              Procurement costs increase when purchases are made at higher
              prices than necessary even though processes are formally followed.
            </p>
          </div>
        </div>

        {/* HOW XYNEX HELPS */}
        <div className="max-w-4xl mx-auto text-left space-y-6">
          <h3 className={`text-2xl font-semibold ${GRADIENT_TEXT}`}>
            How Xynex Identifies It
          </h3>

          <p className="text-gray-200 leading-relaxed">
            Xynex analyzes procurement transactions using variables such as
            material, vendor, unit price, quantity, project, and buying
            frequency. It highlights purchases and vendors that consistently
            operate outside normal price and sourcing behavior. GenAI converts
            complex comparisons into clear business explanations.
          </p>

          <div className="space-y-4">
            <h3 className={`text-2xl font-semibold ${GRADIENT_TEXT}`}>
              The Impact
            </h3>

            <p className="text-gray-200 leading-relaxed">
              Hidden cost inefficiencies are revealed, enabling better sourcing
              decisions and negotiations.
            </p>
          </div>
        </div>
      </section>
      {/* Case 4 */}
      <section className="w-full text-left space-y-24">
        {/* PROBLEM SECTION */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          <div className="space-y-6 text-left">
            <h2 className={`text-3xl md:text-4xl font-bold ${GRADIENT_TEXT}`}>
              Improving Fleet Efficiency in Supply Chain Operations
            </h2>

            <p className="text-gray-200 leading-relaxed">
              The fleet was always busy. Vehicles were moving every day, fuel
              bills were approved, and delivery targets were mostly met. Yet
              logistics costs kept increasing. The Supply Chain Finance Manager
              noticed something unusual—some routes and vehicles were consuming
              more fuel and time than expected, even though delivery volumes
              hadn’t changed much.
            </p>

            <p className="text-gray-200 leading-relaxed">
              On paper, everything looked operationally justified. But when
              costs were compared across vehicles, routes, and drivers, the
              efficiency mix didn’t feel right. The challenge was knowing which
              trips, which vehicles, and which patterns were driving the
              inefficiency.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src={UseCase4}
              alt="Inventory analytics"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* HOW XYNEX HELPS */}
        <div className="max-w-4xl mx-auto text-left space-y-6">
          <h3 className={`text-2xl font-semibold ${GRADIENT_TEXT}`}>
            How Xynex Identifies It
          </h3>

          <p className="text-gray-200 leading-relaxed">
            The team uploaded fleet data into Xynex and selected simple fields
            such as vehicle, distance, fuel consumed, trip count and time. Xynex
            looked at how these factors normally work together and then flagged
            specific trip records where fuel usage, trip frequency, or idle time
            was unusually high for similar routes and loads. It didn’t just show
            averages—it pointed directly to the exact vehicles, routes, and
            trips where efficiency dropped.
          </p>

          <p className="text-gray-200 leading-relaxed">
            Xynex also explained, in clear language, how these records were
            connected to rising logistics costs, making it easy for the team to
            ask the right questions and take corrective action.
          </p>
          <div className="space-y-4">
            <h3 className={`text-2xl font-semibold ${GRADIENT_TEXT}`}>
              The Impact
            </h3>

            <p className="text-gray-200 leading-relaxed">
              Hidden inefficiencies that once blended into daily operations
              became visible. The team could focus on specific routes, vehicles,
              or driving patterns—improving fleet efficiency, controlling fuel
              costs, and strengthening overall supply chain performance.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
  return isInDashboard ? content : <MainLayout>{content}</MainLayout>;
};

export default UseCase;
